import { useState } from "react";
import { motion } from "framer-motion";
import { Check, X, Trophy, RotateCcw } from "lucide-react";

export default function TriviaGame({ questions }) {
  const [answers, setAnswers] = useState({});
  const answered = Object.keys(answers).length;
  const score = questions.reduce((acc, q, i) => acc + (answers[i] === q.answer_index ? 1 : 0), 0);
  const done = answered === questions.length;

  return (
    <div data-testid="trivia-game" className="mt-14 border border-crimson/30 bg-surface">
      <div className="px-6 py-4 border-b border-white/10 flex items-center justify-between">
        <p className="text-xs font-mono uppercase tracking-[0.3em] text-crimson">The Five-Question Gauntlet</p>
        <p className="text-xs font-mono text-ash">{answered}/{questions.length} answered</p>
      </div>

      {questions.map((q, qi) => {
        const revealed = answers[qi] !== undefined;
        return (
          <div key={qi} data-testid={`trivia-question-${qi}`} className="px-6 py-6 border-b border-white/5">
            <p className="font-display font-bold text-lg mb-4 leading-snug">
              <span className="text-crimson font-mono text-sm mr-3">Q{qi + 1}</span>
              {q.question}
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {q.options.map((opt, oi) => {
                const picked = answers[qi] === oi;
                const correct = q.answer_index === oi;
                let cls = "border-white/10 text-porcelain hover:border-crimson/50";
                if (revealed && correct) cls = "border-kinetic bg-kinetic/10 text-kinetic";
                else if (revealed && picked) cls = "border-crimson bg-crimson/10 text-crimson";
                else if (revealed) cls = "border-white/5 text-ash";
                return (
                  <button
                    key={oi}
                    data-testid={`trivia-option-${qi}-${oi}`}
                    disabled={revealed}
                    onClick={() => setAnswers({ ...answers, [qi]: oi })}
                    className={`text-left text-sm border px-4 py-3 transition-colors duration-300 flex items-center justify-between gap-2 ${cls}`}>
                    <span>{opt}</span>
                    {revealed && correct && <Check size={14} className="shrink-0" />}
                    {revealed && picked && !correct && <X size={14} className="shrink-0" />}
                  </button>
                );
              })}
            </div>
            {revealed && q.fact && (
              <motion.p initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} className="text-ash text-sm mt-3 font-serif italic">
                {q.fact}
              </motion.p>
            )}
          </div>
        );
      })}

      {done ? (
        <div data-testid="trivia-score" className="px-6 py-10 text-center">
          <Trophy className="mx-auto text-kinetic mb-4" size={30} />
          <p className="font-display font-black text-5xl">{score} / {questions.length}</p>
          <p className="text-ash text-sm mt-3">
            {score === questions.length
              ? "Flawless. The desk salutes you."
              : score >= 3
                ? "Solid round. Tomorrow's gauntlet is your revenge."
                : "Rough round — a fresh gauntlet lands tomorrow at 5am."}
          </p>
          <button data-testid="trivia-restart-button" onClick={() => setAnswers({})}
            className="mt-6 inline-flex items-center gap-2 border border-white/15 hover:border-kinetic/60 text-porcelain text-xs font-mono uppercase tracking-[0.18em] px-5 py-3 rounded-full transition-colors duration-300">
            <RotateCcw size={13} /> Play again
          </button>
        </div>
      ) : (
        <p className="px-6 py-4 text-[10px] font-mono uppercase tracking-[0.2em] text-ash">
          Answer all five to reveal your score
        </p>
      )}
    </div>
  );
}
